/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useCallback, useMemo } from 'react';
import _, { compact } from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Panel as BootstrapPanel } from 'react-bootstrap';
import { Link } from '@bingads-webui-react/fabric-with-signal';
import { MessageBarType, MessageBar } from '@fluentui/react';
import { Stack, Label } from '@bingads-webui-react/fabric-control';
import { ValidationGroup, ErrorMessages } from '@bingads-webui-react/validation';
import { getLocationTargetTitleDetails, getAdScheduleDetails, getBidTitleDetails } from '@bingads-webui-campaign-react/wizard-shared-components';
import { CustomValidatedDefaultBid } from '@bingads-webui-campaign-react/default-bid';
import { AdGroup } from '@bingads-webui/campaign-odata-schemas';
import { ValidatedLegacyDateSetting, getDateSettingDetails } from '@bingads-webui-campaign/date-setting';
import { ValidatedLocationTargetSetting } from '@bingads-webui-campaign-react/step-wizard-controls';
import { withStepLoadingShell } from '@bingads-webui-campaign-react/step-wizard';
import { ValidatedLegacyAdSchedule } from '@bingads-webui-campaign/ad-schedule';
import { SEARCH_TYPE } from '@bingads-webui-react/bing-maps';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  FormErrorGroup, UnmappedFormErrors,
  entityIs, andComp, propertyIs, anyComp, notComp,
} from '@bingads-webui-campaign-react/form-error';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { ActuationGroup, withActuation, FocusableActuator } from '@bingads-webui-campaign-react/actuator';
import { ERROR_ENTITY, getPanelErrorProps, getConvertedCurrencyDealMinimumBid, getAccountExchangeRateFromLocalStorage } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import {
  Collapse,
  TextTitle,
  useCollapseState,
  ValidatedBootstrapPanel,
  ValidatedFixedPanel,
  ValidatedPanel,
  collapseExpander,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import {
  isXandrDisplayAdsCampaign,
  isXandrVideoAdsCampaign,
  CampaignSubTypes,
  campaignShouldServeOnMSAN,
  isXandrServing,
  ScopeLevel
} from '@bingads-webui-campaign-react/constants';
import { Alert } from '@bingads-webui-react/alert';
import {
  ValidatedCompanyPicker,
  formatCompany,
  ValidatedIndustryPicker,
  formatIndustry,
  ValidatedJobFunctionPicker,
  formatJobFunction,
  PredictiveTargetingHeader,
  PredictiveTargetingControlHeader,
  SectionShimmer,
} from '@bingads-webui-campaign-react/display-plus-native-ads';
import { ValidatedCustomSegmentsPicker, formatCustomSegment } from '@bingads-webui-campaign-react/custom-segments-edit-create';
import { ServerValidatedWebExclusionsWithValidation, webExclusionPropertyVal } from '@bingads-webui-react/web-exclusions';
import { getContentTargetingPlacementsSummaryText, ContentTargetingPlacements } from '@bingads-webui-campaign-react/display-plus-campaigns/src/steps/adgroup/components/content-targeting-placements';
import { ServerValidatedRemainingCharsValidatedTextField } from '../../common/shared-components';
import { useStepAdGroupSettingsState } from './use-step-ad-group-state';
import { AgeRange, formatAgeRanges } from './target/age-range';
import { Gender, formatGenders } from './target/gender';
import { Device, formatDevices } from './target/device';
import { ValidatedAudiencePicker, formatAudience } from './target/audience-picker';
import { ValidatedFrequencyCapping } from './frequency-capping';
import { ValidatedGenreTargets, formatGenreTargets } from './genre-targeting';
import { getGenreOptions, getDealGenreOptions } from './genre-targeting/genre-options';
import { getAdGroupStepStyles } from './step-ad-group-settings-styles';
import { ReachEstimate, tooNarrowFlag, tooNarrowErrorMessage } from './reach-estimate';
import { DealImpressionPanel } from '../../common/deal-potential-impression/deal-potential-impression';
import { getNetflixFrequencyCapping } from './frequency-capping/netflixFrequencyCapping';
import { NetflixTCAgreement } from './netflix-tc/netflix-tc-agreement';

const FocusableErrorMessage = withActuation(ErrorMessages, { Actuators: [FocusableActuator] });

const entityIsAdGroup = entityIs(ERROR_ENTITY.AdGroup);

const adGroupUrlOptionsProperties = notComp(andComp(
  entityIsAdGroup,
  anyComp(
    propertyIs('TrackingUrlTemplate'),
    propertyIs('UrlCustomParameters'),
    propertyIs('FinalUrlSuffix')
  )
));

export const adGroupNameProp = andComp(entityIsAdGroup, propertyIs('Name'));
export const adGroupBidProp = andComp(entityIsAdGroup, anyComp(propertyIs('Bid'), propertyIs('BidAmount')));
function StepAdGroupSettings(props) {
  const {
    stepService,
  } = props;

  const [optimizedTargetingEnabled, setOptimizedTargetingEnabled] = useState(false);

  const [stepState, stepApi] = useStepAdGroupSettingsState(stepService);

  const [adScheduleView, setAdScheduleView] = useState(null);
  const setAdScheduleViewCallback = useCallback((view) => {
    setAdScheduleView(view);
  }, []);

  const {
    i18n,
    legacyI18n,
    hideStepHeader,
    serverContext,
    bundleCountries,
    locationTargets,
    bundleLocationCriterions,
    adSchedules,
    campaignTimeZone,
    genreTargets,
    ageRanges,
    genderTypes,
    deviceTypes,
    deviceRestrictions,
    deviceRestricted,
    companyNameTargets,
    negativeCompanyNameTargets,
    jobTargets,
    negativeJobTargets,
    industry,
    negativeIndustry,
    locationRestricted,
    dealLocationList,
    genreRestricted,
    ageRestricted,
    genderRestricted,
    audienceRestricted,
    campaignBiddingStrategy,
    campaignSubtype,
    scope,
    adGroup,
    adGroupErrors,
    systemErrors,
    navigationConfirmationPrompt,
    reachEstimationTargets,
    serverErrors,
    dealDetails,
    areBeaconsActive,
    showAllValidationErrors,
    campaignSettings,
    customSegments,
    placements,
    exclusions,
    initialAdGroup,
  } = stepState;
  const { onAdGroupValidation } = stepApi;

  const {
    DynamicPermissions: {
      XandrVideoCampaignsAudienceTargets,
      XandrNegativeTarget,
      XandrDeviceTarget,
      GenreTargets,
      ShowNetflixTCAccepted,
      AgeGenderDisclaimer,
      EnableMSANDisplayCampaigns,
      EnableMSANVideoCampaigns,
      EnableKeywordCustomSegments,
      ContentTargeting,
      ContentTargetingMSNSubcategories,
      CampaignLifetimeBudget,
      MaeLifetimeBudgetExclusion,
    },
    Permissions: {
      IsDealFilterEnabled,
    },
    CurrentCustomer: {
      Id: cid,
    },
    CurrentAccount: {
      Id: aid,
    },
    NetflixEnabledCountryCodes = 'GB',
  } = serverContext;

  const shouldServeOnMSAN = campaignShouldServeOnMSAN(campaignSettings);
  const isEnableMSANDisplayCampaignsEnabled = EnableMSANDisplayCampaigns && isXandrDisplayAdsCampaign(campaignSubtype) && shouldServeOnMSAN;
  const isEnableMSANVideoCampaignsEnabled = EnableMSANVideoCampaigns && isXandrVideoAdsCampaign(campaignSubtype) && shouldServeOnMSAN;

  const isMSANEnabled = isEnableMSANDisplayCampaignsEnabled || isEnableMSANVideoCampaignsEnabled;
  const isXandrServingCampaign = isXandrServing(campaignSubtype, campaignSettings);

  const isCampaignLifetimeBudgetEnabled = CampaignLifetimeBudget && !MaeLifetimeBudgetExclusion;

  const basicOptions = compact([
    'ad-group',
    'bid',
    'locations',
    'age-range',
    'gender',
    'start-end-date',
    isMSANEnabled && 'predictive-targeting',
    isMSANEnabled && 'web-exclusions',
    !isMSANEnabled && 'audience',
    !isMSANEnabled && 'device',
    !isMSANEnabled && 'frequency-capping',
    !isMSANEnabled && 'ad-schedule',
    !isMSANEnabled && 'genre-targets',
    !isMSANEnabled && 'netflix-tc',
  ]);

  const [
    allAdGroupSettingsSections,
    adGroupSettingsExpandedSections,
    setAdGroupSettingsExpandedSections,
    flipAdGroupSettingsExpandAllState,
  ] = useCollapseState(
    basicOptions /* all sections */,
    dealDetails ? ['ad-group', 'bid'] : ['ad-group', 'bid', 'locations'] /* initially expanded sections */
  );

  const [
    allAdGroupSettingsTargetingSections,
    adGroupSettingsTargetingExpandedSections,
    setAdGroupSettingsTargetingExpandedSections,
  ] = useCollapseState(
    compact([
      'audience',
      'device',
      'company-target',
      'job-target',
      'industry-target',
      'frequency-capping',
      'ad-schedule',
      'genre-targets',
      'netflix-tc',
      'predictive-targeting-control',
      'start-end-date',
      EnableKeywordCustomSegments && 'custom-segments',
      ContentTargeting && 'content-targeting-placements',
    ]) /* all sections */,
    [] /* initially expanded sections */
  );

  const classes = useDefaultStyles(getAdGroupStepStyles);
  const isDealTypeRestricted = dealDetails && _.isEqual('Restricted', dealDetails.Type);
  const shouldUsePredefinedFrequencyLimit = isDealTypeRestricted;
  const shouldHideDeviceListForDeal = isDealTypeRestricted;

  let bidType = (campaignBiddingStrategy && campaignBiddingStrategy.Type === 'ManualCpm') ? 'CpmBid' : 'CpvBid';
  if (campaignBiddingStrategy.Type === 'EnhancedCpc') {
    bidType = 'SearchBid';
  }

  let bidRange = serverContext.KeywordBidRange;

  if (isXandrServing(campaignSubtype, campaignSettings) && isXandrVideoAdsCampaign(campaignSubtype)) {
    bidRange = bidType === 'CpmBid' ? serverContext.VideoViewsCPMBidRange : serverContext.VideoViewsCPVBidRange;
  }

  if (isXandrDisplayAdsCampaign(campaignSubtype)) {
    bidRange = serverContext.KeywordCPMBidRange;
  }

  const shouldShowDealLocationTarget = IsDealFilterEnabled && dealDetails && !locationRestricted;
  const isDealGenreRestricted = GenreTargets && IsDealFilterEnabled && dealDetails && dealDetails.Genre;
  const genreOptions = useMemo(() => _.sortBy(isDealGenreRestricted ? getDealGenreOptions(i18n) : getGenreOptions(i18n), 'name'), [i18n, isDealGenreRestricted]);

  const isCTVSupported = !isXandrDisplayAdsCampaign(campaignSubtype) && bidType === 'CpvBid';
  const isXandrVideoCampaignsAudienceTargetsEnabled = XandrVideoCampaignsAudienceTargets;

  const shouldShowDeviceTarget = (!dealDetails && bidType !== 'CpvBid' && !isXandrDisplayAdsCampaign(campaignSubtype) && XandrDeviceTarget) || !isXandrServingCampaign;

  let dealMinimumBid = 0;
  if (dealDetails) {
    const exchangeRateMapping = getAccountExchangeRateFromLocalStorage(serverContext);
    dealMinimumBid = getConvertedCurrencyDealMinimumBid(dealDetails.MinimumBid, dealDetails.Currency, serverContext.CurrentAccount.CurrencyCode, exchangeRateMapping.val);
  }

  const bidValidationProps = {
    field: bidType,
    schema: {
      properties: {
        [bidType]: {
          type: 'number',
          minimum: dealDetails ? Math.max(dealMinimumBid, bidRange.FloorValue) : bidRange.FloorValue,
          maximum: dealDetails ? null : bidRange.CeilingValue,
          required: true,
        },
      },
    },
  };

  const reachEstimationToNarrowErrorMessage = useMemo(() => {
    if (adGroup.data.reachEstimation === tooNarrowFlag) {
      return [i18n.getString(tooNarrowErrorMessage)];
    }
    return null;
  }, [adGroup.data.reachEstimation, i18n]);

  const basicSettingsExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setAdGroupSettingsExpandedSections }),
    [setAdGroupSettingsExpandedSections]
  );

  const shouldHideAudiences = !isXandrVideoCampaignsAudienceTargetsEnabled && isCTVSupported;

  const getAdScheduleDetailsCombined = useMemo(() => {
    const adScheduleDetails = getAdScheduleDetails(i18n, adScheduleView && adScheduleView.current, true);

    return ` ${adScheduleDetails}`;
  }, [adScheduleView, i18n]);

  const NetflixEnabledCountryCodesArr = useMemo(() => NetflixEnabledCountryCodes.split(','), [NetflixEnabledCountryCodes]);
  const BundleCountriesCodesArr = useMemo(() => (bundleLocationCriterions && bundleLocationCriterions.map(c => c && c.Criterion && c.Criterion.LocationCriterion && c.Criterion.LocationCriterion.CountryCode)) || [], [bundleLocationCriterions]);

  const webExclusionsError = serverErrors && serverErrors.filter(err => err.property === 'webExclusions');

  const shouldShowNetflixTC = useMemo(() =>
    ShowNetflixTCAccepted &&
    isCTVSupported &&
    locationTargets &&
    (
      locationTargets.currentLevelSettings.targetOption === 'everywhere' ||
      (
        locationTargets.currentLevelSettings.targetOption === 'bundle'
        && BundleCountriesCodesArr.some(bundleCountry => NetflixEnabledCountryCodesArr.indexOf(bundleCountry) >= 0)
      ) ||
      (
        locationTargets.currentLevelSettings.targetOption === 'specific' &&
        _.some(
          locationTargets.currentLevelSettings.currentLocations,
          location => location.SelectionStatus === 'Included'
            && location.Criterion.LocationCriterion
            && NetflixEnabledCountryCodesArr.indexOf(location.Criterion.LocationCriterion.CountryCode) >= 0
        )
      )
    ), [ShowNetflixTCAccepted, isCTVSupported, locationTargets, NetflixEnabledCountryCodesArr, BundleCountriesCodesArr]);

  const isFCapEnabled = useMemo(() => (bidType === 'CpmBid'), [bidType]);

  const shouldSelectAllDeviceByDefault = useMemo(() => {
    if (campaignSubtype === CampaignSubTypes.AudienceStatic || !isXandrServingCampaign) {
      if (adGroup.data && _.isArray(adGroup.data.initialDeviceTypes) && adGroup.data.initialDeviceTypes.length === 0) {
        return true;
      }
    }

    return false;
  }, [campaignSubtype, adGroup, isXandrServingCampaign]);

  const deviceTargetingPanel = useMemo(() => (
    <Collapse.Panel
      key="device"
      title={<TextTitle
        titleKey={_TL_('Device')}
        topicKey="pop_BA_CampaignCreate_AudTargets_Device"
        details={formatDevices({ devices: deviceTypes, i18n, isCTVSupported })}
      />}
    >
      <Device
        i18n={i18n}
        devices={deviceTypes}
        deviceRestrictions={deviceRestrictions}
        isCTVSupported={isCTVSupported}
        defaultSelectAll={shouldSelectAllDeviceByDefault}
      />
    </Collapse.Panel>
  ), [deviceTypes, deviceRestrictions, i18n, isCTVSupported, shouldSelectAllDeviceByDefault]);

  const audienceTargetingPanel = useMemo(() => (
    <Collapse.Panel
      key="audience"
      title={<TextTitle
        titleKey={_TL_('Audience')}
        topicKey="pop_BA_CampaignCreate_AudTargets_Audience"
        details={formatAudience({ audiences: adGroup.data.audiences, i18n })}
      />}
    >
      <ValidatedAudiencePicker
        i18n={i18n}
        campaignSubType={isXandrDisplayAdsCampaign(campaignSubtype) ? CampaignSubTypes.DisplayAds : CampaignSubTypes.BrandAwarenessVideoAds}
        campaignSettings={campaignSettings}
        campaignBiddingStrategy={campaignBiddingStrategy}
        supportExclusion={XandrNegativeTarget}
        validationProps={{
          data: {
            audiences: adGroup.data.audiences,
          },
        }}
        hideCustomSegmentsOption={(adGroup && adGroup.data && adGroup.data.Id) || initialAdGroup === null || (initialAdGroup && initialAdGroup.data && new Date(initialAdGroup.data.StartDate) > new Date())}
      />
    </Collapse.Panel>
  ), [adGroup.data.audiences, i18n, campaignSubtype, campaignSettings, campaignBiddingStrategy, XandrNegativeTarget, initialAdGroup, adGroup]);

  const genreTargetingPanel = useMemo(() => (
    <Collapse.Panel
      key="genre-targets"
      title={<TextTitle
        titleKey={_TL_('Genre')}
        topicKey="pop_BA_CampaignCreate_AudTargets_Genre"
        details={formatGenreTargets({ genreTargets, genreOptions, i18n })}
      />}
    >
      <ValidatedGenreTargets
        i18n={i18n}
        genreTargets={genreTargets}
        genreOptions={genreOptions}
      />
    </Collapse.Panel>
  ), [genreTargets, genreOptions, i18n]);

  const adScheduleTargetingPanel = useMemo(() => (
    <ValidatedPanel
      key="ad-schedule"
      title={<TextTitle
        titleKey={_TL_('Ad schedule')}
        topicKey="pop_BA_Targeting_DayTime"
        details={getAdScheduleDetailsCombined}
      />}
    >
      {
        !dealDetails &&
        <ValidatedLegacyAdSchedule
          isCreation
          hideMinuteOptions
          level="adgroup"
          entity={adGroup}
          validationProps={{
            data: adSchedules,
          }}
          setAdScheduleView={setAdScheduleViewCallback}
          hideUseCampaignSetting
        />
      }
    </ValidatedPanel>
  ), [adGroup, adSchedules, dealDetails, setAdScheduleViewCallback, getAdScheduleDetailsCombined]);

  const getBasicSettingsSection = () => (
    <div className={classNames('basic-ad-group-section', classes.container)}>
      <Stack horizontal tokens={{ childrenGap: 8 }}>
        <Stack>
          <Stack horizontalAlign="end" tokens={{ padding: '0 0 12px 0' }}>
            <Link className="jumbo-card-control-all-btn" onClick={flipAdGroupSettingsExpandAllState}>{adGroupSettingsExpandedSections.length ? i18n.getString(_TL_('Collapse all')) : i18n.getString(_TL_('Expand all'))}</Link>
          </Stack>
          {reachEstimationToNarrowErrorMessage &&
            <div className={classNames(classes.reachEstimationError, 'reach-estimation-errors')}>
              <FocusableErrorMessage
                messages={reachEstimationToNarrowErrorMessage}
                actuationProps={{ needsActivation: true }}
              />
            </div>
          }
          <Collapse allPanelItems={allAdGroupSettingsSections} checkItems skipItemsTypeCheck activeKeys={adGroupSettingsExpandedSections} setActiveKeys={setAdGroupSettingsExpandedSections}>
            <ValidatedFixedPanel
              key="ad-group"
              title={<TextTitle titleKey={_TL_('Ad group name')} topicKey="pop_BA_CampaignCreate_AudTargets_AdGroupName" />}
              attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
            >
              <ServerValidatedRemainingCharsValidatedTextField
                placeholder={i18n.getString(_TL_('Enter a name for your ad group'))}
                id="input-ad-group-name"
                validationProps={{
                  field: 'Name',
                  schema: AdGroup,
                }}
                i18n={i18n}
                formErrorProps={{ filter: adGroupNameProp }}
              />
            </ValidatedFixedPanel>
            <ValidatedFixedPanel
              key="bid"
              title={<TextTitle titleKey={_TL_('Bids & frequency limit')} />}
              attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
              actuationProps={{
                expand: basicSettingsExpand,
                expandKey: 'budget',
              }}
            >
              <Label htmlFor="input-ad-group-bid">
                {i18n.getString(_TL_('Default bid for bid strategy: {{bidStrategy}}'), { bidStrategy: getBidTitleDetails(i18n, campaignBiddingStrategy) })}
                <HelpPopup
                  topic="pop_BA_BidTypeAwareness"
                  aria-label={i18n.getString(_TL_('Click to learn more about bid strategy'))}
                />
              </Label>
              <CustomValidatedDefaultBid
                placeholderText="25.00"
                id="input-ad-group-bid"
                currencyCode={serverContext.CurrentAccount.CurrencyCode}
                i18n={i18n}
                validationProps={bidValidationProps}
                formErrorProps={{ filter: adGroupBidProp }}
              />
              {
                shouldUsePredefinedFrequencyLimit ?
                  getNetflixFrequencyCapping(i18n)
                  :
                  (isFCapEnabled || !(isEnableMSANDisplayCampaignsEnabled || isEnableMSANVideoCampaignsEnabled)) &&
                  <ValidatedFrequencyCapping
                    i18n={i18n}
                    validationProps={{
                      data: adGroup.data.FrequencyCapSettings,
                    }}
                  />
              }
            </ValidatedFixedPanel>
            <ValidatedPanel
              key="start-end-date"
              noOverflowHidden
              title={<TextTitle
                titleKey={_TL_('Start and end dates')}
                details={`${getDateSettingDetails(i18n, adGroup && adGroup.data.StartDate, adGroup && adGroup.data.EndDate, undefined, isCampaignLifetimeBudgetEnabled)}`}
                panelKey="start-end-date"
              />}
              attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
            >
              <ValidatedLegacyDateSetting
                i18n={i18n}
                validationProps={{
                data: {
                  StartDate: adGroup.data.StartDate,
                  EndDate: adGroup.data.EndDate,
                  TimeZone: campaignTimeZone,
                  isCreate: !adGroup.data.Id || initialAdGroup === null || (initialAdGroup && initialAdGroup.data && new Date(initialAdGroup.data.StartDate) > new Date()),
                },
              }}
              />
            </ValidatedPanel>
            {
              !(dealLocationList && dealLocationList.length === 0) && (!shouldHideDeviceListForDeal || shouldShowDealLocationTarget) &&
              <Collapse.Panel
                key="locations"
                disableExpand={locationRestricted}
                title={<TextTitle
                  titleKey={_TL_('Location')}
                  topicKey="pop_BA_Settings_LocationTargeting"
                  details={getLocationTargetTitleDetails(i18n, locationTargets, false, '', true)}
                />}
              >
                <ValidatedLocationTargetSetting
                  validationProps={{
                    data: locationTargets,
                  }}
                  bundleCountries={bundleCountries}
                  bundleLocationCriterions={bundleLocationCriterions}
                  isRadiusExcludeEnabled={!isMSANEnabled}
                  allowNegativeLocationTargeting={XandrNegativeTarget}
                  showSearchTypeOptions={isMSANEnabled}
                  defaultSelectedSearchType={SEARCH_TYPE.Boundary}
                  isBrandAwarenessCampaignScenario={isXandrServingCampaign}
                  hideIntent
                  // hideRadius in restricted mode
                  isAdGroupLevel={!shouldShowDealLocationTarget && (dealLocationList && dealLocationList.length > 0)}
                  scope={scope}
                  bidStrategyType="placeholder"
                  strings={{
                    description: i18n.getString(_TL_('For example, a country/region, state/province, or ZIP/postal code.')),
                  }}
                />
              </Collapse.Panel>
            }
            {
              shouldShowNetflixTC &&
              <ValidatedFixedPanel
                key="netflix-tc"
                title={<TextTitle
                  titleKey={_TL_('Netflix Terms and Conditions')}
                // topicKey="pop_BA_Netflix_Terms_and_Conditions"
                />}
              >
                <NetflixTCAgreement
                  i18n={i18n}
                  cid={cid}
                  aid={aid}
                />
              </ValidatedFixedPanel>
            }
            {
              !ageRestricted && !shouldHideAudiences &&
              <Collapse.Panel
                key="age-range"
                title={<TextTitle
                  titleKey={_TL_('Age')}
                  topicKey="pop_BA_CampaignCreate_AudTargets_Age"
                  details={formatAgeRanges(ageRanges, i18n)}
                />}
              >
                <AgeRange
                  i18n={i18n}
                  ageRanges={ageRanges}
                />
              </Collapse.Panel>
            }
            {
              !genderRestricted && !shouldHideAudiences &&
              <Collapse.Panel
                key="gender"
                title={<TextTitle
                  titleKey={_TL_('Gender')}
                  topicKey="pop_BA_CampaignCreate_AudTargets_Gender"
                  details={formatGenders(genderTypes, i18n)}
                />}
              >
                <Gender
                  i18n={i18n}
                  genders={genderTypes}
                />
              </Collapse.Panel>
            }
            {
              !shouldHideAudiences && (isEnableMSANDisplayCampaignsEnabled || isEnableMSANVideoCampaignsEnabled) &&
              <ValidatedFixedPanel
                key="predictive-targeting"
                title={adGroup ? <PredictiveTargetingHeader data={adGroup.data} /> : <SectionShimmer count={1} />}
                attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
              >
                <div />
              </ValidatedFixedPanel>
            }
            {
              (((dealDetails && !(deviceRestricted && deviceTypes.length === 0) && !shouldHideDeviceListForDeal)) || shouldShowDeviceTarget) &&
              deviceTargetingPanel
            }
            {
              !audienceRestricted && !shouldHideAudiences &&
              (!isEnableMSANDisplayCampaignsEnabled || !isEnableMSANVideoCampaignsEnabled) &&
              audienceTargetingPanel
            }
            {
              ((!genreRestricted && isCTVSupported) || (isDealGenreRestricted)) &&
              (!isEnableMSANDisplayCampaignsEnabled || !isEnableMSANVideoCampaignsEnabled) &&
              genreTargetingPanel
            }
            {
              (!isEnableMSANDisplayCampaignsEnabled || !isEnableMSANVideoCampaignsEnabled) &&
              adScheduleTargetingPanel
            }
            {
              (isEnableMSANDisplayCampaignsEnabled || isEnableMSANVideoCampaignsEnabled) &&
              <ValidatedPanel
                key="web-exclusions"
                title={<TextTitle titleKey={_TL_('Web Exclusions')} />}
                attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                actuationProps={{
                  expand: basicSettingsExpand,
                  expandKey: 'web-exclusions',
                }}
              >
                <span className="web-exclusions-description">
                  {i18n.getString(_TL_('Which websites should not show your ads and which IP addresses should be blocked from seeing your ads?'))}
                </span>
                <ServerValidatedWebExclusionsWithValidation
                  showSiteExclusion
                  parentData={adGroup && adGroup.data}
                  level={ScopeLevel.AdGroup}
                  exclusionData={exclusions || {}}
                  errors={webExclusionsError}
                  formErrorProps={{ filter: webExclusionPropertyVal }}
                />
              </ValidatedPanel>
            }
          </Collapse>
          {
            (isEnableMSANDisplayCampaignsEnabled || isEnableMSANVideoCampaignsEnabled) &&
            <Stack horizontal token={{ childrenGap: 14 }}>
              <ValidatedBootstrapPanel
                defaultExpanded={false}
                className={classNames(classes.collapsiblePanel, 'targeting-preferences-panel')}
                render={() => (
                  <>
                    <BootstrapPanel.Heading>
                      <BootstrapPanel.Title toggle>
                        {i18n.getString(_TL_('Targeting preferences (optional)'))}
                      </BootstrapPanel.Title>
                    </BootstrapPanel.Heading>
                    <BootstrapPanel.Collapse>
                      <BootstrapPanel.Body>
                        <div className={classNames('targeting-preferences-section')}>
                          <Collapse allPanelItems={allAdGroupSettingsTargetingSections} checkItems skipItemsTypeCheck activeKeys={adGroupSettingsTargetingExpandedSections} setActiveKeys={setAdGroupSettingsTargetingExpandedSections}>
                            {
                              (((dealDetails && !(deviceRestricted && deviceTypes.length === 0) && !shouldHideDeviceListForDeal)) || shouldShowDeviceTarget) &&
                              deviceTargetingPanel
                            }
                            {
                              !audienceRestricted && !shouldHideAudiences &&
                              audienceTargetingPanel
                            }
                            {
                              !shouldHideAudiences &&
                              <Collapse.Panel
                                key="company-target"
                                title={<TextTitle
                                  titleKey={_TL_('Company')}
                                  topicKey="pop_BA_CampaignCreate_AudTargets_Company"
                                  details={formatCompany({ companyNameTargets, i18n })}
                                />}
                              >
                                <ValidatedCompanyPicker
                                  i18n={i18n}
                                  classes={classes}
                                  campaignSubType={isXandrDisplayAdsCampaign(campaignSubtype) ? CampaignSubTypes.DisplayAds : CampaignSubTypes.BrandAwarenessVideoAds}
                                  validationProps={{
                                    data: {
                                      companyNameTargets,
                                      negativeCompanyNameTargets,
                                    },
                                  }}
                                />
                              </Collapse.Panel>
                            }
                            {
                              !shouldHideAudiences &&
                              <Collapse.Panel
                                key="industry-target"
                                title={<TextTitle
                                  titleKey={_TL_('Industry')}
                                  validationProps={{
                                    data: {
                                      industry,
                                      negativeIndustry,
                                    },
                                  }}
                                  details={formatIndustry({ industry, i18n })}
                                />}
                              >
                                <ValidatedIndustryPicker
                                  i18n={i18n}
                                  classes={classes}
                                  campaignSubType={isXandrDisplayAdsCampaign(campaignSubtype) ? CampaignSubTypes.DisplayAds : CampaignSubTypes.BrandAwarenessVideoAds}
                                />
                              </Collapse.Panel>
                            }
                            {
                              !shouldHideAudiences &&
                              <Collapse.Panel
                                key="job-target"
                                title={<TextTitle
                                  titleKey={_TL_('Job')}
                                  topicKey="pop_BA_CampaignCreate_AudTargets_Company"
                                  details={formatJobFunction({ jobTargets, i18n })}
                                />}
                              >
                                <ValidatedJobFunctionPicker
                                  i18n={i18n}
                                  classes={classes}
                                  campaignSubType={isXandrDisplayAdsCampaign(campaignSubtype) ? CampaignSubTypes.DisplayAds : CampaignSubTypes.BrandAwarenessVideoAds}
                                  validationProps={{
                                    data: {
                                      jobTargets,
                                      negativeJobTargets,
                                    },
                                  }}
                                />
                              </Collapse.Panel>
                            }
                            {
                              ((!genreRestricted && isCTVSupported) ||
                                (isDealGenreRestricted))
                              &&
                              genreTargetingPanel
                            }
                            {adScheduleTargetingPanel}
                            {EnableKeywordCustomSegments &&
                              <ValidatedPanel
                                key="custom-segments"
                                title={<TextTitle
                                  titleKey={_TL_('Custom segments')}
                                  key="custom-segments"
                                  topicKey="pop_BA_Audiences_CustomSegments"
                                  panelKey="custom-segments"
                                  details={formatCustomSegment({ customSegments, i18n })}
                                  headingRole="heading"
                                  headingLevel="2"
                                />}
                                noOverflowHidden
                              >
                                <ValidatedCustomSegmentsPicker
                                  i18n={i18n}
                                  validationProps={{
                                    data: customSegments,
                                  }}
                                />
                              </ValidatedPanel>
                            }
                            {ContentTargeting &&
                              <ValidatedPanel
                                key="content-targeting-placements"
                                title={<TextTitle
                                  titleKey={_TL_('Placements')}
                                  key="content-targeting-placements"
                                  topicKey="pop_BA_AdPlacements"
                                  panelKey="content-targeting-placements"
                                  details={getContentTargetingPlacementsSummaryText({ i18n, placements })}
                                  noOverflowHidden
                                />}
                              >
                                <ContentTargetingPlacements
                                  msnSubcategoriesEnabled={ContentTargetingMSNSubcategories}
                                  validationProps={{
                                  data: {
                                    placements: placements || [],
                                  },
                                }}
                                />
                              </ValidatedPanel>
                            }
                            {
                              !shouldHideAudiences &&
                              <ValidatedFixedPanel
                                key="predictive-targeting-control"
                                title={adGroup ? (
                                  <PredictiveTargetingControlHeader
                                    validationProps={{
                                      data: adGroup.data,
                                    }}
                                    onToggleEnabledChanged={setOptimizedTargetingEnabled}
                                    EnableMSANDisplayCampaigns={EnableMSANDisplayCampaigns}
                                    EnableMSANVideoCampaigns={EnableMSANVideoCampaigns}
                                  />)
                                  : <SectionShimmer count={1} />
                                }
                                attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                              >
                                <MessageBar
                                  messageBarType={MessageBarType.warning}
                                  isMultiline
                                >
                                  {!optimizedTargetingEnabled ?
                                    i18n.getString(_TL_('Predictive targeting is automatically enabled. You can disable it by setting a targeting preference.'))
                                    : i18n.getString(_TL_('Turning off predictive targeting significantly limits our ability to help you reach the best audience for your campaign.'))
                                  }

                                </MessageBar>
                              </ValidatedFixedPanel>
                            }
                          </Collapse>
                        </div>
                      </BootstrapPanel.Body>
                    </BootstrapPanel.Collapse>
                  </>
                )}
              />
            </Stack>
          }
        </Stack>
        <div className={classes.reachEstimate}>
          {
            dealDetails &&
            <DealImpressionPanel
              targets={reachEstimationTargets}
              i18n={i18n}
              serverContext={serverContext}
              deal={dealDetails}
            />
          }
          {
            !dealDetails &&
            <ReachEstimate
              i18n={i18n}
              shouldServeOnMSAN={isMSANEnabled}
              permissions={serverContext.Permissions}
              targets={reachEstimationTargets}
              accountId={serverContext.CurrentAccount.Id}
              customerId={serverContext.CurrentCustomer.Id}
              validationProps={{ field: 'reachEstimation' }}
            />
          }
        </div>
      </Stack>
    </div>);

  return (
    <>
      <ActuationGroup actuationProps={{ isActive: areBeaconsActive }}>
        <AttentionBeaconGroup>
          <FormErrorGroup formErrorProps={{ errors: serverErrors, showAllValidationErrors }}>
            {!hideStepHeader &&
              <>
                <h1>{i18n.getString(_TL_('Ad group'))}</h1>
                <span>{i18n.getString(_TL_(''))}</span>
              </>
            }
            <div className="ad-group-server-errors">
              {/* // TODO: add error handling for "CampaignServiceDuplicateAdGroupCriterion" */}
              <ErrorMessages messages={adGroupErrors} />
            </div>
            <div className="other-server-errors">
              <UnmappedFormErrors filter={adGroupUrlOptionsProperties} />
            </div>

            <div className="system-errors">
              <ErrorMessages messages={systemErrors} />
            </div>
            <ValidationGroup
              validationProps={{
                schema: AdGroup,
                onValidate: onAdGroupValidation,
                data: adGroup.data,
                i18n: legacyI18n,
              }}
            >
              {getBasicSettingsSection()}
            </ValidationGroup>
            {navigationConfirmationPrompt}
            {AgeGenderDisclaimer && <Alert
              message={i18n.getString(_TL_('If you’re advertising in connection with any financial, insurance, education, career and employment, and/or housing services, you should <b>not</b> use individuals’ demographics—such as age, gender, location, etc.—for the purpose of personalizing advertising, segmenting, or profiling customers.'))}
              level="Info"
              icon={(<div className="alert-image" />)}
              dismissible={false}
            />}
          </FormErrorGroup>
        </AttentionBeaconGroup>
      </ActuationGroup>
    </>
  );
}

StepAdGroupSettings.propTypes = {
  stepService: PropTypes.shape({}).isRequired,
};

const withLoadingShell = withStepLoadingShell(StepAdGroupSettings);

export { withLoadingShell as StepAdGroupSettings };
